import { bindCommand, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { Component, ComponentPolicy, component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { GeomUtil } from '../../../platform/geom/GeomUtil';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { AdapterInfo, CreateAdapterInfo } from '../../canvas/AdapterInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { DropInfo, DropTarget } from '../../canvas/entities/DropTarget';
import { Physical, PotentialInteraction, RelativeTo } from '../../canvas/entities/Physical';
import { RectangularPhysical } from '../../canvas/entities/RectangularPhysical';
import { Secret } from '../../canvas/entities/Secret';
import { Snappable } from '../../canvas/entities/Snappable';
import { Graphic } from '../graphics/Graphic';
import { Sticky } from '../paper/Sticky';
import { Pingable } from '../presentation/Pingable';
import { Token } from '../tokens/Token';
import { voterInsert, voterResetOrReveal, voterVote } from './VoterCommands';
import { VoterItem } from './VoterItem';
import { VoterLayout } from './VoterLayout';
import { voterPropertyLayout } from './VoterPropertyLayout';
import { VoterTitleField } from './VoterTitleField';
import { VoterVote } from './VoterVote';

const invocation = defineCommand(
    'voterInvocation',
    null,
    (cc, [id, hint]: [string, string?]) => {

        if (hint === 'revealReset') {
            cc.dispatch(voterResetOrReveal, [id]);
        }

        if (hint.startsWith('item')) {
            const index = parseInt(hint.slice(4));
            cc.dispatch(voterVote, [id, index]);
        }
    },
    [],
);

const mutation = defineCommand(
    'voterMutation',
    null,
    (r, [id]: [string]) => {
        r.call(EditorSession).update(AdapterInfo, CreateAdapterInfo(id, 'field'));
    },
    [],
);

const voterDef = {
    name: 'Voter',
    implements: [Physical, Snappable, Pingable, DropTarget, Secret],
    propertyLayout: voterPropertyLayout,
    invocation,
    mutation,
};

const voterTransformHints = Object.freeze({
    scaleX: true,
    scaleY: true,
    rotate: false,
    translate: true,
    visible: true,
});

const voterComponentPolicy = ComponentPolicy.restricted('VoterItem', 'VoterVote');

export type VoterState = 'voting' | 'results';

@component(voterDef)
export class Voter extends RectangularPhysical implements DropTarget {

    @property({ type: 'string', default: '' })
    public title: string;

    @property({ type: 'string', default: '#c3352b' })
    public color: string;

    @property({ type: 'string' })
    public sound: string;

    @property({ type: 'string', default: 'voting' })
    public state: VoterState;

    public get boundary() {
        return true;
    }

    @memoUsing('children')
    public get items() {
        return this.polarize(VoterItem);
    }

    @memoUsing('items')
    public get votes() {
        return this.polarize(VoterVote);
    }

    public get winners() {
        const { votes } = this;
        const counts = new Map<string, number>();
        for (const v of votes) {
            const count = counts.get(v.itemId) || 0;
            counts.set(v.itemId, count + 1);
        }
        const max = Math.max(...Array.from(counts.values()));
        const ids = Array.from(counts.entries()).filter(([itemId, count]) => count === max).map(([itemId]) => itemId);
        return ids.map(id => this.items.find(x => x.id === id));
    }

    @memoUsing('width', 'height', 'state', 'items', 'votes', 'title')
    public get layout() {
        return VoterLayout(this);
    }

    @memoUsing('layout')
    public get uiFields() {
        return [
            VoterTitleField(this),
        ];
    }

    public get shouldSnap() {
        return true;
    }

    protected get policy(): ComponentPolicy {
        return voterComponentPolicy;
    }

    public queryDrop(mpt: Point, subjects: Iterable<Component>): DropInfo {
        // Supports dropping tokens, stickies and graphics...
        for (const s of subjects) {
            if (!s.is(Sticky) && !s.is(Token) && !s.is(Graphic)) {
                return null;
            }
        }

        const toModel = this.transform(RelativeTo.Model);
        const rpt = this.toRelative(mpt);

        if (this.layout.base.contains(rpt)) {
            if (this.layout.items.length > 0) {

                const insertSpots = [
                    ...this.layout.items.map((tm, i) => ({
                        index: i,
                        guide: tm.lines().at(3).translate(-5, 0),
                    })),
                    {
                        index: this.layout.items.length,
                        guide: Rect.fromMany(this.layout.items).lines().at(1).translate(5, 0)
                    }
                ];

                const { index, guide } = GeomUtil.nearestTo(rpt, insertSpots, i => i.guide.mid);

                return {
                    target: this,
                    guides: [{ points: toModel.apply(guide).toArray() }],
                    command: bindCommand(voterInsert, [this.id, index, Array.from(subjects).map(s => s.id)]),
                };

            }
            else {

                const guide = this.layout.inner;

                return {
                    target: this,
                    guides: [{ points: toModel.apply(guide) }],
                    command: bindCommand(voterInsert, [this.id, 0, Array.from(subjects).map(s => s.id)]),
                };
            }

        }
        else {
            return null;
        }


    }

    public queryTransformHints() {
        return voterTransformHints;
    }

    public testInteraction({ type, mpt }: PotentialInteraction): [boolean, string] {
        const { items, revealReset } = this.layout;
        const rpt = this.toRelative(mpt);
        if (this.localBounds.contains(rpt)) {
            if (type === 'invoke') {
                if (this.state === 'voting') {
                    for (const tm of items) {
                        if (tm.contains(rpt)) {
                            return [true, `item${items.indexOf(tm)}`];
                        }
                    }
                }
                if (revealReset.contains(rpt)) {
                    return [true, 'revealReset'];
                }
            }
            if (type !== 'invoke') {
                return [true, 'default'];
            }
        }
        return [false, 'none'];
    }

    // protected setTransformationResult(x: number, y: number, r: number, w: number, h: number): void {
    //     w = Math.min(1600, Math.max(400, w));
    //     h = Math.min(1600, Math.max(400, h));
    //     super.setTransformationResult(x, y, r, w, h);
    // }
}

